import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, X, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";
import { usePublicPlans, PublicPlan } from "@/hooks/usePublicPlans";

const PricingSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createCheckout } = useSubscription();
  const [isYearly, setIsYearly] = useState(false);
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const { plans: dbPlans, loading } = usePublicPlans();

  // Check if user email is verified
  const isEmailVerified = user?.email_confirmed_at != null;

  const handleCtaClick = async (planKey: string, plan?: PublicPlan) => {
    if (!user) {
      navigate("/signup");
      return;
    }

    // Free plan - go to dashboard
    if (planKey === "free") {
      navigate("/dashboard");
      return;
    }

    // Paid plans - check email verification then checkout directly
    if (!isEmailVerified) {
      toast.error(t("subscription.emailVerificationRequired"));
      return;
    }

    // 直接調用 Stripe checkout
    if (!plan) return;
    
    const priceId = isYearly ? plan.yearlyStripePriceId : plan.monthlyStripePriceId;
    if (!priceId) {
      toast.error(t("subscription.noPriceId") || "Price not configured");
      return;
    }

    try {
      setProcessingPlan(planKey);
      await createCheckout(priceId);
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(t("subscription.checkoutError") || "Checkout failed");
    } finally {
      setProcessingPlan(null);
    }
  };

  // Free 方案現在從資料庫讀取，不再硬編碼

  // 比較表特性 - 動態生成
  const getFeatureValue = (plan: PublicPlan | null, featureKey: string) => {
    if (!plan) return false;
    const limits = plan.limits as Record<string, unknown>;
    
    switch (featureKey) {
      case "sceneModes":
        return "4";
      case "scrollModes":
        return true;
      case "scripts":
        return limits.maxScripts === -1 ? t("pricingPage.unlimited") : String(limits.maxScripts || 0);
      case "charsPerScript":
        return limits.maxCharactersPerScript === -1 ? t("pricingPage.unlimited") : 
          new Intl.NumberFormat().format(limits.maxCharactersPerScript as number || 0);
      case "customFonts":
        return !!limits.hasCustomFonts;
      case "mirrorMode":
        return !!limits.hasMirrorMode;
      case "exportScripts":
        return !!limits.hasExport;
      case "noWatermark":
        return !!limits.hasNoWatermark;
      case "voiceSync":
        return limits.hasVoiceSync ? t("pricingPage.comingSoon") : false;
      case "offlineMode":
        return !!limits.hasOfflineMode;
      case "prioritySupport":
        return !!limits.hasPrioritySupport;
      default:
        return false;
    }
  };

  const comparisonFeatures = [
    "sceneModes", "scrollModes", "scripts", "charsPerScript",
    "customFonts", "mirrorMode", "exportScripts", "noWatermark",
    "voiceSync", "offlineMode", "prioritySupport"
  ];


  const getPrice = (plan: PublicPlan) => {
    if (isYearly && plan.yearlyPrice != null) {
      return (plan.yearlyPrice / 12).toFixed(2);
    }
    return plan.monthlyPrice?.toFixed(2) || "0";
  };

  if (loading) {
    return (
      <section id="pricing" className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </section>
    );
  }

  return (
    <section id="pricing" className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <h2 className="heading-section text-center mb-8">
          {t("pricing.title")}
        </h2>

        {/* Toggle */}
        <div className="flex items-center justify-center gap-4 mb-12">
          <span className={`font-medium ${!isYearly ? "text-foreground" : "text-muted-foreground"}`}>
            {t("pricing.monthly")}
          </span>
          <button
            onClick={() => setIsYearly(!isYearly)}
            className={`relative w-14 h-7 rounded-full transition-colors ${
              isYearly ? "bg-primary" : "bg-gray-300"
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-md transition-transform ${
                isYearly ? "translate-x-7" : "translate-x-0"
              }`}
            />
          </button>
          <span className={`font-medium ${isYearly ? "text-foreground" : "text-muted-foreground"}`}>
            {t("pricing.yearly")}
          </span>
        </div>

        {/* Pricing Cards - 全部從資料庫讀取 */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mb-20">
          {/* Dynamic Plan Cards - 包含 Free */}
          {dbPlans.map((plan) => (
            <div
              key={plan.id}
              className={`${plan.colorClass} rounded-2xl p-6 md:p-8 border-2 border-foreground relative card-hover flex flex-col h-full`}
            >
              {plan.isPopular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-foreground text-background px-4 py-1 rounded-full text-sm font-bold">
                  {t("pricingPage.mostPopular")}
                </div>
              )}

              <h3 className="heading-card mb-2">
                {plan.displayName}
              </h3>

              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-4xl md:text-5xl font-bold">
                  ${getPrice(plan)}
                </span>
                <span className="text-foreground/70">
                  {t("pricing.perMonth")}
                </span>
              </div>

              <ul className="space-y-3 mb-8 flex-grow">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button 
                onClick={() => handleCtaClick(plan.name, plan)}
                disabled={processingPlan === plan.name}
                className="w-full btn-outline bg-background/50 hover:bg-foreground hover:text-background mt-auto text-center justify-center disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {processingPlan === plan.name ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("subscription.processing") || "Processing..."}
                  </>
                ) : (
                  plan.ctaText
                )}
              </button>
            </div>
          ))}
        </div>

        {/* Feature Comparison Table */}
        <div className="max-w-5xl mx-auto">
          <div className="overflow-x-auto">
            <table className="w-full border-2 border-foreground rounded-2xl overflow-hidden">
              <thead>
                <tr className="bg-foreground text-background">
                  <th className="text-left p-4 font-semibold">{t("pricingPage.feature")}</th>
                  {dbPlans.map((plan) => (
                    <th key={plan.id} className="text-center p-4 font-semibold">
                      {plan.displayName}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((featureKey, index) => (
                  <tr key={featureKey} className={index % 2 === 0 ? "bg-background" : "bg-muted/30"}>
                    <td className="p-4 font-medium">{t(`pricingPage.features.${featureKey}`)}</td>
                    {dbPlans.map((plan) => {
                      const value = getFeatureValue(plan, featureKey);
                      return (
                        <td key={plan.id} className="p-4 text-center">
                          {typeof value === "boolean" ? (
                            value ? <Check className="w-5 h-5 mx-auto text-green-600" /> : <X className="w-5 h-5 mx-auto text-foreground/30" />
                          ) : (
                            <span>{value}</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};


export default PricingSection;
